/** メッセージ表示: 送信者ラベル・@メンション強調・MAC→dashリンク */

import type { Message } from "../types.ts";
import { dashMacUrl } from "./dashLinks.ts";

/** タイムライン1件の見た目の種別（改善 B-4）。
 *  色は CSS 側（.support-msg--*）が持ち、ここでは意味だけを返す。
 *  送受信で左右に振り分けず左寄せ一列にし、左端の色帯で区別する。 */
export type MessageVariant = "customer" | "outbound" | "auto" | "internal" | "system";

export type MessageBubbleStyle = {
  label: string;
  variant: MessageVariant;
  internal: boolean;
};

export function messageBubbleStyle(m: Pick<Message, "source" | "visibility">): MessageBubbleStyle {
  const fromCustomer = m.source === "email";
  const isSystem = m.source === "system";
  const internal = !fromCustomer && !isSystem && (m.visibility ?? "internal") === "internal";

  if (isSystem) return { label: "システム", variant: "system", internal: true };
  if (fromCustomer) return { label: "お客様", variant: "customer", internal: false };
  if (internal) return { label: "社内メモ（顧客未送信）", variant: "internal", internal: true };
  if (m.source === "auto_ack" || m.source === "urgent_ack" || m.source === "intake_ack") {
    return { label: "自動返信（受付確認）", variant: "auto", internal: false };
  }
  if (m.source === "status_notify") return { label: "ステータス通知（顧客へ送信）", variant: "outbound", internal: false };
  if (m.source === "correction") return { label: "訂正（顧客へ送信）", variant: "outbound", internal: false };
  return { label: "顧客へ送信", variant: "outbound", internal: false };
}

export function formatMessageAuthor(author: string): string {
  const a = (author || "").trim();
  if (!a) return "—";
  if (a.includes("@")) return a;
  if (a.startsWith("ops:")) return a.replace(/^ops:/, "") + " (legacy)";
  return a;
}

export function isOwnMessage(
  author: string,
  currentEmail: string | null,
  currentRole?: string | null,
): boolean {
  const a = (author || "").trim().toLowerCase();
  if (!a) return false;
  if (currentEmail && a === currentEmail.trim().toLowerCase()) return true;
  // 移行期: ops:super 等の legacy 表記。ログイン中ロールと一致するときのみ自分扱い
  if (currentRole && a === `ops:${currentRole.trim().toLowerCase()}`) return true;
  return false;
}

const MENTION_SPLIT = /(@[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}|@[a-zA-Z0-9._+-]+)/g;

export function splitMessageWithMentions(text: string): Array<{ kind: "text" | "mention"; value: string }> {
  if (!text) return [];
  const parts: Array<{ kind: "text" | "mention"; value: string }> = [];
  let last = 0;
  for (const m of text.matchAll(MENTION_SPLIT)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push({ kind: "text", value: text.slice(last, idx) });
    parts.push({ kind: "mention", value: m[0] });
    last = idx + m[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts.length ? parts : [{ kind: "text", value: text }];
}

export type MentionUser = { email: string; label: string; role?: string };

/** メッセージ内の添付: 画像 ![name](url) / 動画 [🎬 …] / ファイル [📎 …] */
const ATTACHMENT_RE =
  /!\[([^\]]*)\]\((\/api\/campaign\/photo\?p=[^)]+)\)|\[🎬 ([^\]]*)\]\((\/api\/campaign\/photo\?p=[^)]+)\)|\[📎 ([^\]]*)\]\((\/api\/campaign\/photo\?p=[^)]+)\)/g;

const MAC_RE = /\b([0-9A-Fa-f]{2}(?::[0-9A-Fa-f]{2}){5})\b/g;

function splitTextWithMacs(text: string): Array<{ kind: "text" | "mac"; value: string }> {
  if (!text) return [];
  const parts: Array<{ kind: "text" | "mac"; value: string }> = [];
  let last = 0;
  for (const m of text.matchAll(MAC_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) parts.push({ kind: "text", value: text.slice(last, idx) });
    parts.push({ kind: "mac", value: m[1] });
    last = idx + m[0].length;
  }
  if (last < text.length) parts.push({ kind: "text", value: text.slice(last) });
  return parts.length ? parts : [{ kind: "text", value: text }];
}

export type MessagePart =
  | { kind: "text"; value: string }
  | { kind: "mention"; value: string }
  | { kind: "mac"; value: string; href: string }
  | { kind: "image"; value: string; name: string }
  | { kind: "video"; value: string; name: string }
  | { kind: "file"; value: string; name: string };

function expandTextPart(value: string): MessagePart[] {
  const out: MessagePart[] = [];
  for (const seg of splitTextWithMacs(value)) {
    if (seg.kind === "mac") {
      out.push({ kind: "mac", value: seg.value, href: dashMacUrl(seg.value) });
    } else {
      out.push({ kind: "text", value: seg.value });
    }
  }
  return out;
}

function expandMentionParts(text: string): MessagePart[] {
  const out: MessagePart[] = [];
  for (const p of splitMessageWithMentions(text)) {
    if (p.kind === "mention") out.push(p);
    else out.push(...expandTextPart(p.value));
  }
  return out;
}

export function parseMessageParts(text: string): MessagePart[] {
  if (!text) return [];
  const parts: MessagePart[] = [];
  let last = 0;
  for (const m of text.matchAll(ATTACHMENT_RE)) {
    const idx = m.index ?? 0;
    if (idx > last) {
      parts.push(...expandMentionParts(text.slice(last, idx)));
    }
    if (m[1] !== undefined && m[2]) {
      parts.push({ kind: "image", name: m[1] || "image", value: m[2] });
    } else if (m[3] !== undefined && m[4]) {
      parts.push({ kind: "video", name: m[3] || "video", value: m[4] });
    } else if (m[5] !== undefined && m[6]) {
      parts.push({ kind: "file", name: m[5] || "file", value: m[6] });
    }
    last = idx + m[0].length;
  }
  if (last < text.length) {
    parts.push(...expandMentionParts(text.slice(last)));
  }
  return parts.length ? parts : expandMentionParts(text);
}
